import { useCallback, useEffect, useRef, useState } from 'react';
import { FeatureFlagQueries } from '@frontend/api-feature-flags';
import { PaymentIntent } from '@frontend/api-weave-pay';
import {
  ITerminalStrategyError,
  PaymentsTerminalController,
  PaymentsTerminalError,
  TerminalPaymentStatus,
} from '@frontend/payments-terminal-controller';
import { PickPartial, Prettify } from '@frontend/types';

type PaymentIntentInfo = { paymentIntent?: PaymentIntent; paymentId?: string };

type UseTerminalPaymentSessionProps = Prettify<
  PickPartial<
    Omit<
      Parameters<typeof PaymentsTerminalController.createTerminalSession>[number],
      'onError' | 'onPaymentStatusChange' | 'enableServerDriven'
    >,
    'reader' | 'paymentsUrl' | 'sdkFailIfReaderInUse'
  >
> & {
  onPaymentSuccess?: () => void;
  invoiceId: string | undefined;
  paymentIntent: PaymentIntentInfo | undefined;
  goBack: () => void;
  retryCreatePaymentIntent: () => Promise<{
    intent?: PaymentIntentInfo;
    paymentIntentError?: ITerminalStrategyError;
  }>;
};
type TerminalActions = Awaited<ReturnType<typeof PaymentsTerminalController.createTerminalSession>>;

type CollectAndProcessPaymentArgs = Parameters<
  Awaited<ReturnType<(typeof PaymentsTerminalController)['createTerminalSession']>>['collectAndProcessPayment']
>[0];

export const RESTRICTED_TERMINAL_STATES: TerminalPaymentStatus[] = ['success', 'processing'];

export const useTerminalPaymentSession = ({
  reader,
  paymentsUrl,
  locationId,
  appData,
  onPaymentSuccess,
  paymentIntent,
  invoiceId,
  goBack,
  retryCreatePaymentIntent,
}: UseTerminalPaymentSessionProps) => {
  const [terminalActions, setTerminalActions] = useState<TerminalActions>();
  const [error, setError] = useState<ITerminalStrategyError>();
  const [status, setStatus] = useState<TerminalPaymentStatus>('not_ready');
  const mounted = useRef(true);

  const { aggregateValue: enableServerDriven } = FeatureFlagQueries.useAggregateFeatureFlagQuery({
    flagName: 'payments:nwx:server-driven-terminals',
    locationIds: [locationId],
  });

  // to ensure only one terminal payment happens, set to false to allow retries
  const terminalPaymentStarted = useRef(false);

  const initializePaymentSession = useCallback(async () => {
    let initializedActions: TerminalActions | undefined;
    let initializationError: ITerminalStrategyError | undefined;

    if (!reader || !paymentsUrl || !locationId) {
      return { initializedActions, initializationError };
    }

    if (RESTRICTED_TERMINAL_STATES.includes(status)) {
      return { initializedActions, initializationError };
    }

    try {
      // If terminalActions is already set, cancel the current action
      await cancelPayment();

      initializedActions = await PaymentsTerminalController.createTerminalSession({
        locationId,
        paymentsUrl,
        reader,
        appData,
        enableServerDriven,
        onPaymentStatusChange(status) {
          if (!mounted.current) return;
          setStatus(status);
          if (status === 'success') {
            onPaymentSuccess?.();
          }
        },
      });
      setTerminalActions(initializedActions);
    } catch (error) {
      if (error instanceof PaymentsTerminalError) {
        initializationError = { action: 'initialize', message: error.message };
      } else {
        initializationError = { action: 'initialize', message: 'An unexpected error occurred' };
      }
      setStatus('not_ready');
      setError(initializationError);
    }

    return { initializedActions, initializationError };
  }, [reader, paymentsUrl, locationId, enableServerDriven]);

  useEffect(() => {
    initializePaymentSession();
  }, [initializePaymentSession]);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const collectAndProcessPayment = useCallback(
    async (
      paymentIntent: CollectAndProcessPaymentArgs['paymentIntent'],
      paymentId: CollectAndProcessPaymentArgs['paymentId'],
      invoiceId?: CollectAndProcessPaymentArgs['invoiceId'],
      actions?: TerminalActions,
      considerError = true
    ) => {
      actions = actions ?? terminalActions;
      if ((considerError && error) || !actions) {
        setStatus('not_ready');
        setError({ action: 'collect', message: 'Terminal session not initialized' });
        return;
      }

      // Check needs to be here to prevent duplicate collects from running
      if (terminalPaymentStarted.current) {
        return;
      }

      try {
        terminalPaymentStarted.current = true;
        return await actions?.collectAndProcessPayment({ paymentIntent, paymentId, invoiceId });
      } catch (error) {
        if (!mounted.current) return;
        setStatus('not_ready');
        if (error instanceof PaymentsTerminalError) {
          if (error.code === 'canceled') return; // good idea?
          setError(error);
        } else if (error instanceof Error) {
          console.log('error in collectAndProcessPayment', error);
          setError({ action: 'collect', message: error.message });
        } else {
          setError({ action: 'collect', message: 'An unexpected error occurred' });
        }
        return;
      }
    },
    [terminalActions, error]
  );

  const retryPayment = async (
    paymentIntent: CollectAndProcessPaymentArgs['paymentIntent'],
    paymentId: CollectAndProcessPaymentArgs['paymentId'],
    invoiceId: CollectAndProcessPaymentArgs['invoiceId']
  ) => {
    setError(undefined);
    terminalPaymentStarted.current = false;

    let actions = terminalActions;
    if (!actions) {
      const { initializedActions, initializationError } = await initializePaymentSession();
      if (initializationError) return;
      else actions = initializedActions;
    }

    await collectAndProcessPayment(paymentIntent, paymentId, invoiceId, actions, false);
  };

  const cancelPayment = useCallback(() => {
    setError(undefined);
    terminalPaymentStarted.current = false;
    if (!terminalActions) {
      // do nothing because terminal session not created yet
      return Promise.resolve();
    }
    return terminalActions?.cancelCurrentAction();
  }, [terminalActions?.cancelCurrentAction]);

  // Cancel payment on unmount
  const cancelPaymentOnUnmount = useCallback(() => {
    if (terminalPaymentStarted.current) {
      cancelPayment().catch((error) => {
        if (!mounted.current) return;
        if (error instanceof PaymentsTerminalError) {
          setError(error);
        } else {
          setError({ action: 'cancel', message: 'An unexpected error occurred' });
        }
      });
    }
  }, [cancelPayment]);

  useEffect(() => {
    window.addEventListener('beforeunload', cancelPaymentOnUnmount);
    return () => {
      window.removeEventListener('beforeunload', cancelPaymentOnUnmount);
      cancelPaymentOnUnmount();
    };
  }, [cancelPaymentOnUnmount]);

  // Store reader when successful
  const storeOnce = useRef(false);
  useEffect(() => {
    if (!paymentsUrl || status !== 'success' || !reader || !locationId || storeOnce.current) {
      return;
    }
    PaymentsTerminalController.storeReader(locationId, paymentsUrl, reader);
    storeOnce.current = true;
  }, [status, locationId, paymentsUrl, reader]);

  // INITIATE COLLECTION
  useEffect(() => {
    if (!paymentIntent?.paymentIntent || !paymentIntent.paymentId || !collectAndProcessPayment || error) {
      return;
    }
    collectAndProcessPayment(paymentIntent.paymentIntent, paymentIntent.paymentId, invoiceId);
  }, [paymentIntent?.paymentIntent, collectAndProcessPayment, error]);

  const handleCancel = async () => {
    try {
      await cancelPayment();
    } catch (error) {
      // TODO: do nothing?
      console.log('Error cancelling payment', { error });
    } finally {
      goBack();
    }
  };

  const handleTryAgain = async () => {
    let intent = paymentIntent;
    try {
      if (!intent?.paymentIntent || !intent.paymentId) {
        const { intent: newPaymentIntent, paymentIntentError } = await retryCreatePaymentIntent();

        if (paymentIntentError) throw paymentIntentError;
        else intent = newPaymentIntent;
      }
      if (intent?.paymentIntent && intent.paymentId) {
        await retryPayment(intent.paymentIntent, intent.paymentId, invoiceId);
      }
    } catch (error) {
      console.log('Error retrying payment', { error });
    }
  };

  return {
    cancelPayment,
    collectAndProcessPayment: terminalActions?.collectAndProcessPayment ? collectAndProcessPayment : undefined,
    error,
    retryPayment,
    status,
    handleCancel,
    handleTryAgain,
  };
};
